import { formatInTimeZone } from 'date-fns-tz'
import { DEFAULT_TIMEZONE } from './env'

export function formatDateTime(value: string | null | undefined, timeZone: string = DEFAULT_TIMEZONE): string {
  if (!value) return '-'
  try {
    return formatInTimeZone(new Date(value), timeZone, 'd MMM yyyy — h:mm a')
  } catch {
    return value
  }
}

export function formatDate(value: string | null | undefined, timeZone: string = DEFAULT_TIMEZONE): string {
  if (!value) return '-'
  try {
    return formatInTimeZone(new Date(value), timeZone, 'd MMM yyyy')
  } catch {
    return value
  }
}

/** Formats a plain (date-only, e.g. "2026-10-12") date without any timezone conversion -- check_in/check_out are calendar dates, not instants, so shifting them through a timezone (like formatDate does) can print the wrong day. */
export function formatDateOnly(value: string | null | undefined): string {
  if (!value) return '-'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!m) return value
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(d)
}

export function formatRelative(value: string | null | undefined): string {
  if (!value) return '-'
  const then = new Date(value).getTime()
  const now = Date.now()
  const diffMs = now - then
  const diffMin = Math.round(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} min ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr} hr ago`
  const diffDay = Math.round(diffHr / 24)
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`
}

/** Local <input type="datetime-local"> value <-> ISO helpers, always anchored to a specific IANA timezone rather than the browser's. */
export function toDatetimeLocalInTz(iso: string | null | undefined, timeZone: string = DEFAULT_TIMEZONE): string {
  if (!iso) return ''
  return formatInTimeZone(new Date(iso), timeZone, "yyyy-MM-dd'T'HH:mm")
}

export function fromDatetimeLocalInTz(local: string, timeZone: string = DEFAULT_TIMEZONE): string | null {
  if (!local) return null
  // Interpret the naive local wall-clock string as being in `timeZone`,
  // then convert to a real UTC instant. Uses Intl to compute the zone's
  // current offset for that date rather than depending on the browser tz.
  const [datePart, timePart] = local.split('T')
  const [y, m, d] = datePart.split('-').map(Number)
  const [hh, mm] = (timePart || '00:00').split(':').map(Number)
  const utcGuess = new Date(Date.UTC(y, m - 1, d, hh, mm))
  const tzString = formatInTimeZone(utcGuess, timeZone, 'XXX') // e.g. +02:00
  const sign = tzString.startsWith('-') ? -1 : 1
  const [offH, offM] = tzString.slice(1).split(':').map(Number)
  const offsetMinutes = sign * (offH * 60 + offM)
  const utcMillis = Date.UTC(y, m - 1, d, hh, mm) - offsetMinutes * 60000
  return new Date(utcMillis).toISOString()
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : plural || singular + 's'}`
}
